import { useOrganizationContext } from '@/contexts/organization-context';
import SettingsTabTitle from '../settings-tab-title';
import ColorList from './color-list';
import { gql } from '@/__generated__';
import {
  ApolloError,
  skipToken,
  useMutation,
  useSuspenseQuery,
} from '@apollo/client';
import { Button } from '@/components/shadcn/button';
import { Spinner } from '@/components/basic/spinner';
import { useState } from 'react';
import { toast } from '@/components/shadcn/use-toast';
import { logError } from '@/lib/rum';
import {
  Fonts as FontsType,
  Media,
  OrganizationGetByIdSuccess,
} from '@/__generated__/graphql';
import Fonts from './fonts';
import Logos from './logos';
import { cleanTypeName } from '@/lib/utils';
import { useSessionContext } from 'supertokens-auth-react/recipe/session';
import { hasAdminRole } from '@/lib/auth-service';

const BrandSettingsQuery = gql(`
  query BrandSettingsQuery($input: OrganizationGetByIdInput!) {
      organization {
          getById(input: $input) {
          ... on OrganizationGetByIdSuccess {
            result {
            ...BrandLogosFragment
            ...BrandColorsFragment
            ...BrandFontsFragment
          }
        }
      }
    } 
  }
`);

const OrganizationBrandUpdate = gql(`
  mutation OrganizationBrandUpdate($input: OrganizationBrandUpdateInput!) {
    organization {
      brandUpdate(input: $input) {
        ... on OrganizationBrandUpdateResult {
          result {
            id
          }
        }
      }
    }
  }
`);

interface BrandMetaData {
  brandColors: string[];
  fonts: FontsType;
  logos: Media[];
}

export function BrandTab() {
  const { organizationId } = useOrganizationContext();
  const [updates, setUpdates] = useState<Partial<BrandMetaData>>({});
  const session = useSessionContext();

  const isAdmin =
    !session.loading &&
    session.doesSessionExist &&
    hasAdminRole(session.accessTokenPayload['st-role']?.v);

  const { data: brandData, error } = useSuspenseQuery(
    BrandSettingsQuery,
    organizationId
      ? {
          variables: {
            input: {
              id: organizationId,
            },
          },
        }
      : skipToken
  );

  const [updateOrganization, { loading: updateOrganizationLoading }] =
    useMutation(OrganizationBrandUpdate, {
      onCompleted: (data) => {
        toast({
          description: 'Brand settings succesfully saved',
          duration: 3000,
        });
      },
      onError: (error: ApolloError) => {
        logError(`Organization update failed: ${error.message}`);
        toast({
          description: error.message,
          duration: 3000,
          variant: 'destructive',
        });
      },
    });

  const handleSaveSettings = async () => {
    await updateOrganization({
      variables: {
        input: {
          organizationId: organizationId || '',
          brandUpdateInput: {
            brandColors: updates?.brandColors,
            fonts: updates?.fonts,
            logos: updates?.logos?.map((logo) => ({
              id: logo?.id,
            })),
          },
        },
      },
      refetchQueries: ['BrandSettingsQuery'],
    });

    setUpdates({});
  };

  const handleUpdateColors = (brandColors: string[]) => {
    setUpdates((prevState) => ({
      ...prevState,
      brandColors,
    }));
  };

  const handleUpdateFonts = (type: string, fontId: string | null) => {
    const fonts = cleanTypeName(
      (brandData?.organization.getById as OrganizationGetByIdSuccess).result
        .fonts
    );
    setUpdates((prevState) => ({
      ...prevState,
      fonts: {
        ...fonts,
        [type]: fontId,
      },
    }));
  };

  const handleUpdateLogo = (logos: Media[] | null) => {
    if (!logos) {
      return null;
    }
    setUpdates((prevState) => ({
      ...prevState,
      logos: logos,
    }));
  };

  if (!organizationId) {
    return (
      <div>
        <SettingsTabTitle title='Brand' />
        <p>Please select an organization first.</p>
      </div>
    );
  }

  if (error) {
    logError(
      `Something went wrong while loading organization brand settings: ${error.message}`,
      error
    );
  }

  if (
    brandData?.organization.getById.__typename !== 'OrganizationGetByIdSuccess'
  ) {
    return null;
  }

  return (
    <div>
      <SettingsTabTitle title='Brand' />
      <Logos
        logos={brandData?.organization.getById.result}
        onLogoChange={handleUpdateLogo}
      />
      {isAdmin && (
        <>
          <ColorList
            brandColors={brandData?.organization.getById.result}
            onUpdateColors={handleUpdateColors}
          />
          <Fonts
            fonts={brandData.organization.getById.result}
            onChange={(type, fontId) => handleUpdateFonts(type, fontId)}
          />
        </>
      )}
      <div className='mt-4 flex justify-end' onClick={handleSaveSettings}>
        <Button disabled={updateOrganizationLoading} variant={'maggie-primary'}>
          {updateOrganizationLoading ? (
            <Spinner color='white' className='w-6' />
          ) : (
            'Save'
          )}
        </Button>
      </div>
    </div>
  );
}
